import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getExhibitionSettings, isUserLoggedIn, notLoggedAndLoginMandatory } from '@app/app.reducer';
import { ProductBookingLoginGuard } from '@core/data-access/services/booking/product-booking-login-guard';
import { Store } from '@ngrx/store';
import { PreferredTariffValidationState } from '@products/models/tariff.model';
import { TariffCountValidationService } from '@products/services/tariff-count-validation.service';
import { AppConstants } from '@shared/app-constants';
import {
  getAllBookedProductsCount,
  getBookedTariffCount,
  getSharedBookedPersonIdTariffsTotalCount
} from '@store/products/booking/product/product-booking.selectors';
import { getBookedPromoCodeVoucherTariffsCount } from '@store/products/booking/product/voucher/voucher-booking.selectors';
import { getPreferredTariff } from '@store/products/product-selection/product-selection.selectors';
import {
  getAvailableTariffs,
  selectUserAccountTariffLimit,
  selectUserAccountTariffSpecificLimits
} from '@store/products/status/tariff/tariff-status.selectors';
import { getWorkshopsAvailableSeatsGroupedByWorkshopId } from '@store/products/status/workshop/workshop-status.selectors';
import { isEmpty, isNil, isNull } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PreferredTariffValidationService {
  private readonly store = inject(Store);
  private readonly tariffCountValidationService = inject(TariffCountValidationService);
  private readonly productBookingLoginGuard = inject(ProductBookingLoginGuard);

  preferredTariffValidationState$: Observable<Partial<PreferredTariffValidationState>>;

  private preferredTariffValidationServiceState: Partial<PreferredTariffValidationState>;
  private readonly _preferredTariffValidationState$: BehaviorSubject<Partial<PreferredTariffValidationState>> =
    new BehaviorSubject({
      ticketTypeId: null,
      ticketPersonTypeId: null,
      count: 0,
      countDifference: 0,
      validatedCount: 0,
      previousValidatedCount: 0,
      validatedMaxLimit: 0,
      isCountOverLimit: false,
      isTariffSoldOut: false,
      isAvailableTariffsLimitReached: false,
      numberOfBookedVouchers: null,
      numberOfAllBookedTariffs: null,
      numberOfAllBookedCurrentTariffs: null,
      initialNumberOfAvailableTariffs: null,
      currentNumberOfAvailableTariffs: null,
      tariffLimit: null,
      maxTariffLimit: null,
      tariffLimitPerUserAccount: null,
      tariffSpecificLimitPerUserAccount: null,
      currentUserAccountTariffLimit: null,
      isCurrentUserAccountTariffLimitReached: false,
      hideWorkshopTariffCounter: false,
      allowedWorkshopsSeatsAvailable: null,
      areAllAllowedWorkshopsAssigned: false,
      isWorkshopsSelectionMandatoryAndHasWorkshops: false,
      isVoucher: false,
      isVoucherIncludedPerUserAccountLimit: false,
      tariffLimitWarningMessage: ''
    });

  constructor() {
    this.preferredTariffValidationState$ = this._preferredTariffValidationState$.asObservable();
    this.preferredTariffValidationState$
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((tariffValidationState) => {
        this.preferredTariffValidationServiceState = {
          ...this.preferredTariffValidationServiceState,
          ...tariffValidationState
        };
      });
  }

  validatePreferredTariffCount$(
    ticketTypeId: number,
    ticketPersonTypeId: number,
    count: number
  ): Observable<{ ticketTypeId: number; ticketPersonTypeId: number; validatedCount: number }> {
    if (!count) {
      return of({
        ticketTypeId,
        ticketPersonTypeId,
        validatedCount: AppConstants.PREFERRED_PRODUCT_DEFAULT_AMOUNT
      });
    }

    const bookingAndStatusState$ = combineLatest({
      workshopAvailableSeats: this.store.select(getWorkshopsAvailableSeatsGroupedByWorkshopId),
      currentUserAccountTariffLimit: this.store.select(selectUserAccountTariffLimit),
      currentUserAccountTariffSpecificLimits: this.store.select(selectUserAccountTariffSpecificLimits),
      currentAvailableTariffs: this.store.select(getAvailableTariffs),
      bookedProductsCount: this.store.select(getAllBookedProductsCount),
      numberOfBookedVouchers: this.store.select(getBookedPromoCodeVoucherTariffsCount)
    });

    const waitForUserAccountTicketSpecificLimitsResponse = 500;

    return combineLatest({
      isUserLoggedIn: this.store.select(isUserLoggedIn),
      notLoggedAndLoginMandatory: this.store.select(notLoggedAndLoginMandatory),
      exhibitionSettings: this.store.select(getExhibitionSettings),
      tariff: this.store.select(getPreferredTariff(ticketTypeId, ticketPersonTypeId)),
      sharedBookedPersonIdTariffsTotalCount: this.store.select(getSharedBookedPersonIdTariffsTotalCount([])),
      bookingAndStatusState: bookingAndStatusState$
    }).pipe(
      map((combinedLatest) => ({
        ...combinedLatest,
        isCurrentTariffBookingEnabled: this.productBookingLoginGuard.isCurrentTariffBookingEnabled(
          combinedLatest.tariff,
          combinedLatest.exhibitionSettings.ticketLimitPerUserAccount
        )
      })),
      tap(({ isCurrentTariffBookingEnabled }) => {
        if (isCurrentTariffBookingEnabled) {
          return;
        }

        this.productBookingLoginGuard.showLogin();
      }),
      filter(({ isCurrentTariffBookingEnabled }) => isCurrentTariffBookingEnabled),
      switchMap((combinedLatest) =>
        bookingAndStatusState$.pipe(
          debounceTime(waitForUserAccountTicketSpecificLimitsResponse),
          map((bookingAndStatusState) => ({ ...combinedLatest, bookingAndStatusState }))
        )
      ),
      first(
        ({
          notLoggedAndLoginMandatory,
          exhibitionSettings,
          tariff,
          bookingAndStatusState: {
            workshopAvailableSeats,
            currentUserAccountTariffLimit,
            currentUserAccountTariffSpecificLimits,
            currentAvailableTariffs
          }
        }) => {
          const isValid =
            !notLoggedAndLoginMandatory &&
            !isNil(tariff) &&
            !isEmpty(exhibitionSettings) &&
            !isEmpty(workshopAvailableSeats) &&
            !isEmpty(currentAvailableTariffs);

          const isUserAccountTariffLimitRequired =
            Boolean(exhibitionSettings.ticketLimitPerUserAccount) || tariff.hasUserAccountTicketLimit;

          if (isUserAccountTariffLimitRequired) {
            const isCurrentUserAccountTariffLimitSet = !isNull(currentUserAccountTariffLimit);
            const isCurrentTariffSpecificUSerAccountLimitSet = !isNull(currentUserAccountTariffSpecificLimits);
            const isUserAccountTariffLimitSet =
              isCurrentUserAccountTariffLimitSet || isCurrentTariffSpecificUSerAccountLimitSet;

            return isValid && isUserAccountTariffLimitSet;
          }

          return isValid;
        }
      ),
      switchMap(
        ({
          exhibitionSettings,
          tariff,
          sharedBookedPersonIdTariffsTotalCount,
          bookingAndStatusState: {
            workshopAvailableSeats,
            currentUserAccountTariffLimit,
            currentUserAccountTariffSpecificLimits,
            currentAvailableTariffs,
            bookedProductsCount,
            numberOfBookedVouchers
          }
        }) =>
          of(tariff).pipe(
            withLatestFrom(
              this.store.select(
                getBookedTariffCount(
                  tariff.ticketTypeId,
                  tariff.ticketPersonId,
                  tariff.voucherCode,
                  tariff.packageNumber,
                  tariff.packageIndex
                )
              )
            ),
            map(([tariff, currentBookedTariffCount]) => ({
              tariff,
              exhibitionSettings,
              sharedBookedPersonIdTariffsTotalCount,
              numberOfBookedVouchers,
              bookedProductsCount,
              currentUserAccountTariffLimit,
              currentUserAccountTariffSpecificLimits,
              workshopAvailableSeats,
              currentAvailableTariffs,
              currentBookedTariffCount
            }))
          )
      ),
      map(
        ({
          tariff,
          exhibitionSettings,
          sharedBookedPersonIdTariffsTotalCount,
          numberOfBookedVouchers,
          bookedProductsCount,
          currentUserAccountTariffLimit,
          currentUserAccountTariffSpecificLimits,
          currentAvailableTariffs,
          workshopAvailableSeats,
          currentBookedTariffCount
        }) => {
          const { ticketPersonId, isVoucher, availableTickets, ticketLimit, allowedWorkshops } = tariff;

          if (isVoucher) {
            return {
              ticketTypeId,
              ticketPersonTypeId,
              validatedCount: 0
            };
          }

          const {
            limitBoughtTickets,
            ticketLimitPerUserAccount,
            isWorkshopsSelectionMandatory,
            isVoucherIncludedPerUserAccountLimit
          } = exhibitionSettings;

          const currentUserAccountTariffLimits = currentUserAccountTariffSpecificLimits[ticketPersonId];
          const currentUserAccountTariffSpecificLimit =
            currentUserAccountTariffLimits?.ticketLimit ?? ticketLimitPerUserAccount;
          const currentUserAccountTariffSpecificAvailableTickets =
            currentUserAccountTariffLimits?.availableTickets ?? currentUserAccountTariffLimit;
          const numberOfAllBookedCurrentTariffs = sharedBookedPersonIdTariffsTotalCount[ticketPersonId] || 0;

          const { allowedWorkshopsSeatsAvailable, hideWorkshopTariffCounter, areAllAllowedWorkshopsAssigned } =
            this.tariffCountValidationService.tariffAllowedWorkshopsStatus(count, tariff, workshopAvailableSeats);

          this.initAndValidateState({
            ticketTypeId,
            ticketPersonTypeId,
            count,
            validatedCount: currentBookedTariffCount,
            previousValidatedCount: currentBookedTariffCount,
            isTariffSoldOut: this.tariffCountValidationService.isTariffSoldOut(
              currentBookedTariffCount,
              availableTickets
            ),
            isAvailableTariffsLimitReached: this.tariffCountValidationService.isTariffSoldOut(
              currentBookedTariffCount,
              currentAvailableTariffs[ticketPersonId]
            ),
            numberOfBookedVouchers,
            numberOfAllBookedTariffs: bookedProductsCount,
            numberOfAllBookedCurrentTariffs,
            initialNumberOfAvailableTariffs: availableTickets,
            currentNumberOfAvailableTariffs: currentAvailableTariffs[ticketPersonId],
            percentageOfAvailableTariffs: this.tariffCountValidationService.calculatePercentageOfAvailableTariffs(
              count,
              ticketLimit,
              currentAvailableTariffs[ticketPersonId]
            ),
            tariffLimit: ticketLimit,
            maxTariffLimit: limitBoughtTickets,
            tariffLimitPerUserAccount: currentUserAccountTariffSpecificLimit,
            tariffSpecificLimitPerUserAccount: currentUserAccountTariffLimits?.ticketLimit,
            currentUserAccountTariffLimit: currentUserAccountTariffSpecificAvailableTickets,
            isCurrentUserAccountTariffLimitReached:
              this.tariffCountValidationService.isCurrentUserAccountTariffLimitReached(
                currentUserAccountTariffSpecificLimit,
                currentUserAccountTariffSpecificAvailableTickets
              ),
            hideWorkshopTariffCounter: hideWorkshopTariffCounter,
            allowedWorkshopsSeatsAvailable,
            areAllAllowedWorkshopsAssigned,
            isWorkshopsSelectionMandatoryAndHasWorkshops: isWorkshopsSelectionMandatory && allowedWorkshops.length > 0,
            isVoucher: isVoucher,
            isVoucherIncludedPerUserAccountLimit: isVoucherIncludedPerUserAccountLimit,
            tariffLimitWarningMessage: ''
          });

          return {
            ticketTypeId,
            ticketPersonTypeId,
            validatedCount: this.preferredTariffValidationServiceState.validatedCount
          };
        }
      )
    );
  }

  initAndValidateState(tariffValidationState: Partial<PreferredTariffValidationState>) {
    this.validate(tariffValidationState);

    this._preferredTariffValidationState$.next({
      ...this.preferredTariffValidationServiceState,
      ...tariffValidationState
    });
  }

  updateState(tariffValidationState: Partial<PreferredTariffValidationState>) {
    this._preferredTariffValidationState$.next({
      ...this.preferredTariffValidationServiceState,
      ...tariffValidationState
    });
  }

  validateState(count: number, tariffValidationState: Partial<PreferredTariffValidationState>) {
    tariffValidationState.count = count;

    this.validate(tariffValidationState);

    this._preferredTariffValidationState$.next({
      ...this.preferredTariffValidationServiceState,
      ...tariffValidationState
    });
  }

  getValidatedState() {
    return this.preferredTariffValidationServiceState;
  }

  geValidatedStateProperty(propertyName: string) {
    return propertyName in this.preferredTariffValidationServiceState
      ? this.preferredTariffValidationServiceState[propertyName]
      : null;
  }

  private validate(tariffValidationState: Partial<PreferredTariffValidationState>) {
    const { count, validatedCount } = tariffValidationState;

    tariffValidationState.countDifference = count - validatedCount;
    tariffValidationState.previousValidatedCount = validatedCount;

    this.tariffCountValidationService.validateIsAvailableTariffsLimit(tariffValidationState);
    this.tariffCountValidationService.validateTariffLimitPerUserAccount(tariffValidationState);
    // this.tariffCountValidationService.validateWorkshopTariff(tariffValidationState);
    this.tariffCountValidationService.validateMaxTariffLimit(tariffValidationState);
    this.tariffCountValidationService.validateInLimitCounter(count, tariffValidationState);
  }
}
